import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { Badge } from './shared';
import Icon from './shared/Icon';
import navLogo from '../assets/blue-lyf-transparent-1024.png';
import { useSiteNavHidden } from '../utils/siteNavAutoHide';
import useUnreadNotificationCount from '../hooks/useUnreadNotificationCount';
import { useBottomTabBarVisible } from '../utils/bottomTabBar';
import { SETTINGS_TABS } from './SettingsModal/TabNavigation';

const SettingsModal = lazy(() => import('./SettingsModal'));

// Tab ids the ?settings=<tab> deep link accepts (derived from the modal's own
// tab list so the two can't drift).
const VALID_SETTINGS_TABS = SETTINGS_TABS.map(tab => tab.id);
const NotificationCenter = lazy(() => import('./NotificationCenter'));

function Navigation() {
  const { t } = useTranslation();
  const location = useLocation();
  // Hidden on phones while a page asks for the whole screen (the trip
  // planner — see siteNavAutoHide.js). display:none rather than a transform:
  // pages that measure the space above them re-measure on the resize.
  const pageWantsWholeScreen = useSiteNavHidden();
  // Phones with the bottom tab bar get no top nav at all (2026-10-01): the
  // tab bar and the You page carry everything this bar held there.
  const tabBarShowing = useBottomTabBarVisible();
  const hiddenOnPhones = pageWantsWholeScreen || tabBarShowing;
  const navigate = useNavigate();

  // Use Auth Context instead of local state
  const { currentUser, isAdmin, authLoading, currentLocation, logout } = useAuth();
  const { hasMilestone, isLoading: onboardingLoading } = useOnboarding();

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState(null);
  const [settingsInitialSection, setSettingsInitialSection] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Live unread count (shared with the phone tab bar's You badge)
  const unreadCount = useUnreadNotificationCount(currentUser);

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/');
  };

  const handleNotificationClose = () => {
    setShowNotifications(false);
    // No manual refresh: mark-as-read / remove already call notifySubscribers,
    // which updates unreadCount via the subscription in the effect above.
  };

  // Handle Settings modal close
  const handleSettingsClose = () => {
    setShowSettings(false);
    setSettingsInitialTab(null);
  };

  // Deep-linkable settings: any route can carry ?settings=<tabId> to open the
  // Settings modal on that tab (used by notification action_urls and the
  // profile page's owner-mode edit affordances). An optional
  // &section=<id> scrolls that section into view once the tab renders — the
  // Profile tab is long enough that a field near the bottom (home location)
  // is otherwise undiscoverable from a link that promised to go there.
  // Both params are consumed and stripped so refresh/back don't re-open the
  // modal.
  //
  // ?notifications=1 opens the Notification Center the same way — the phone
  // You page's way in, since phones have no top bar to hold the bell.
  useEffect(() => {
    if (!currentUser) return;
    const params = new URLSearchParams(location.search);
    const tab = params.get('settings');
    const openSettings = !!tab && VALID_SETTINGS_TABS.includes(tab);
    const openNotifications = params.get('notifications') === '1';
    if (!openSettings && !openNotifications) return;
    if (openSettings) {
      setSettingsInitialTab(tab);
      setSettingsInitialSection(params.get('section') || null);
      setShowSettings(true);
      params.delete('settings');
      params.delete('section');
    }
    if (openNotifications) {
      setShowNotifications(true);
      params.delete('notifications');
    }
    const qs = params.toString();
    navigate(`${location.pathname}${qs ? `?${qs}` : ''}`, { replace: true });
  }, [currentUser, location.pathname, location.search, navigate]);

  // Only show navigation items when user is logged in (MVP phase)
  // Show NEW badges for features the user hasn't explored yet
  // Labels live in src/locales/en/common.json under nav.items.<key>.label —
  // this array keeps only the non-translatable data (route, icon, badge flag).
  // (same rule as BottomTabBar's dots: cleared by the first visit, hidden
  // until onboarding state has loaded)
  const isNew = (milestone) => !onboardingLoading && !hasMilestone(milestone);
  const navItems = currentUser ? [
    { path: '/', key: 'home', icon: 'home' },
    { path: '/social-feed', key: 'discover', icon: 'target', showNew: isNew('visitedDiscover') },
    { path: '/activity-planner', key: 'planner', icon: 'calendar', showNew: isNew('visitedPlanner') },
    { path: '/my-saved-items', key: 'saved', icon: 'bookmark', showNew: isNew('visitedSavedItems') },
    ...(isAdmin ? [
      { path: '/admin', key: 'admin', icon: 'shield' }
    ] : [])
  ] : [];

  if (authLoading) {
    return (
      <nav className="bg-surface shadow-elevation-2 border-b border-border">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src={navLogo} alt={t('nav.logoAlt')} className="h-10" />
            </Link>
            <div className="text-content-muted">{t('nav.loading')}</div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className={`bg-surface shadow-elevation-2 border-b border-border ${hiddenOnPhones ? 'max-md:hidden' : ''}`}>
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center">
            <img src={navLogo} alt={t('nav.logoAlt')} className="h-10" />
          </Link>

          <div className="flex items-center space-x-4">
            {/* Location Display */}
            {currentUser && (
              <div className="hidden lg:flex items-center space-x-2 text-sm text-content-secondary">
                <Icon name="mapPin" className="w-4 h-4" />
                <span>{currentLocation}</span>
              </div>
            )}

            <div className="hidden md:flex space-x-8">
              {navItems.map(item => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative flex items-center space-x-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'text-primary bg-primary-lighter'
                        : 'text-content-primary hover:text-primary hover:bg-background'
                    }`}
                  >
                    <Icon name={item.icon} className="w-4 h-4" />
                    <span>{t(`nav.items.${item.key}.label`)}</span>
                    {item.showNew && !isActive && (
                      <Badge variant="new" size="sm" pulse className="ml-1" />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Notifications */}
            {currentUser && (
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(true)}
                  className={`relative p-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-content-primary hover:text-primary transition-colors focus-visible:outline-2 focus-visible:outline-primary-light focus-visible:outline-offset-2 rounded-lg ${
                    unreadCount > 0 ? 'animate-pulse' : ''
                  }`}
                  aria-label={unreadCount > 0 ? t('nav.notifications.ariaLabel', { count: unreadCount }) : t('nav.notifications.ariaLabelDefault')}
                >
                  <Icon name="bell" className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute top-0.5 right-0.5 sm:-top-1 sm:-right-1 bg-error text-content-inverse text-xs rounded-full h-5 w-5 flex items-center justify-center font-semibold shadow-elevation-2">
                      {unreadCount > 9 ? t('nav.notifications.countCap') : unreadCount}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* User Menu or Auth Buttons */}
            {currentUser ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 p-1 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 text-content-primary hover:text-primary focus-visible:outline-2 focus-visible:outline-primary-light focus-visible:outline-offset-2 rounded-lg"
                  aria-label={t('nav.userMenu.ariaLabel')}
                  aria-expanded={showUserMenu}
                >
                  {currentUser.profilePicture ? (
                    <img
                      src={currentUser.profilePicture}
                      alt={currentUser.name}
                      className="w-9 h-9 sm:w-8 sm:h-8 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-9 h-9 sm:w-8 sm:h-8 bg-primary-lighter rounded-full flex items-center justify-center">
                      <span className="text-primary font-medium">
                        {currentUser.name ? currentUser.name.charAt(0).toUpperCase() : t('nav.userMenu.avatarFallbackInitial')}
                      </span>
                    </div>
                  )}
                  <span className="hidden md:block font-medium">{currentUser.name}</span>
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 max-w-[calc(100vw-2rem)] bg-surface rounded-lg shadow-elevation-3 py-1 z-modal border border-border">
                    <Link
                      to="/profile"
                      className="flex items-center gap-2 px-4 py-3 sm:py-2 text-sm text-content-primary hover:bg-background"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Icon name="user" className="w-4 h-4 text-content-secondary" /> {t('nav.userMenu.profile')}
                    </Link>
                    <button
                      onClick={() => {
                        setShowSettings(true);
                        setShowUserMenu(false);
                      }}
                      className="w-full text-left flex items-center gap-2 px-4 py-3 sm:py-2 text-sm text-content-primary hover:bg-background"
                    >
                      <Icon name="settings" className="w-4 h-4 text-content-secondary" /> {t('nav.userMenu.settings')}
                    </button>
                    <Link
                      to="/advertise"
                      className="flex items-center gap-2 px-4 py-3 sm:py-2 text-sm text-content-primary hover:bg-background"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Icon name="megaphone" className="w-4 h-4 text-content-secondary" /> {t('nav.userMenu.advertise')}
                    </Link>
                    <div className="border-t border-border my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left flex items-center gap-2 px-4 py-3 sm:py-2 text-sm text-content-primary hover:bg-background"
                    >
                      <Icon name="signOut" className="w-4 h-4 text-content-secondary" /> {t('nav.userMenu.signOut')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden md:flex items-center space-x-2">
                <Link
                  to="/for-businesses"
                  className="text-content-secondary hover:text-primary px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {t('nav.authLinks.desktop.forBusinesses')}
                </Link>
                <Link
                  to="/signin"
                  className="text-content-primary hover:text-primary px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {t('nav.authLinks.desktop.signIn')}
                </Link>
                <Link
                  to="/signup"
                  className="bg-primary hover:bg-primary-dark text-content-inverse px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {t('nav.authLinks.desktop.signUp')}
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="md:hidden p-2 min-w-[44px] min-h-[44px] flex items-center justify-center text-content-secondary hover:text-content-primary focus-visible:outline-2 focus-visible:outline-primary-light focus-visible:outline-offset-2 rounded-lg"
              aria-label={showMobileMenu ? t('nav.hamburger.closeAriaLabel') : t('nav.hamburger.openAriaLabel')}
              aria-expanded={showMobileMenu}
            >
              <svg className="h-6 w-6 fill-current" viewBox="0 0 24 24">
                {showMobileMenu ? (
                  <path fillRule="evenodd" clipRule="evenodd" d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z"/>
                ) : (
                  <path fillRule="evenodd" d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z"/>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {showMobileMenu && (
          <div className="md:hidden border-t border-border py-2 relative z-overlay">
            {navItems.map(item => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-4 py-3 text-base font-medium transition-colors ${
                    isActive
                      ? 'text-primary bg-primary-lighter'
                      : 'text-content-primary hover:text-primary hover:bg-background active:bg-background'
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Icon name={item.icon} className="w-5 h-5" />
                  <span>{t(`nav.items.${item.key}.label`)}</span>
                  {item.showNew && !isActive && (
                    <Badge variant="new" size="sm" pulse />
                  )}
                </Link>
              );
            })}

            {currentUser && (
              <div className="border-t border-border mt-2 pt-2">
                {/* Profile is the flagship surface post-convergence — it
                    shouldn't hide behind the avatar dropdown on mobile */}
                <Link
                  to="/profile"
                  className={`flex items-center space-x-2 px-4 py-3 text-base font-medium transition-colors ${
                    location.pathname === '/profile'
                      ? 'text-primary bg-primary-lighter'
                      : 'text-content-primary hover:text-primary hover:bg-background active:bg-background'
                  }`}
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Icon name="user" className="w-5 h-5" />
                  <span>{t('nav.userMenu.profile')}</span>
                </Link>
                <Link
                  to="/advertise"
                  className="flex items-center space-x-2 px-4 py-3 text-base font-medium text-content-primary hover:text-primary hover:bg-background active:bg-background"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <Icon name="megaphone" className="w-4 h-4" />
                  <span>{t('nav.mobileMenu.advertise')}</span>
                </Link>
              </div>
            )}

            {!currentUser && (
              <div className="border-t border-border mt-2 pt-2">
                <Link
                  to="/for-businesses"
                  className="block px-4 py-3 text-base font-medium text-content-primary hover:text-primary hover:bg-background active:bg-background"
                  onClick={() => setShowMobileMenu(false)}
                >
                  {t('nav.authLinks.mobile.forBusinesses')}
                </Link>
                <Link
                  to="/signin"
                  className="block px-4 py-3 text-base font-medium text-content-primary hover:text-primary hover:bg-background active:bg-background"
                  onClick={() => setShowMobileMenu(false)}
                >
                  {t('nav.authLinks.mobile.signIn')}
                </Link>
                <Link
                  to="/signup"
                  className="block px-4 py-3 text-base font-medium text-content-inverse bg-primary hover:bg-primary-dark active:bg-primary-dark mx-4 rounded-lg text-center mt-2"
                  onClick={() => setShowMobileMenu(false)}
                >
                  {t('nav.authLinks.mobile.signUp')}
                </Link>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Settings Modal */}
      {showSettings && (
        <Suspense fallback={null}>
          <SettingsModal
            isOpen
            onClose={handleSettingsClose}
            initialTab={settingsInitialTab}
            initialSection={settingsInitialSection}
          />
        </Suspense>
      )}

      {/* Notification Center */}
      {showNotifications && (
        <Suspense fallback={null}>
          <NotificationCenter
            isOpen={showNotifications}
            onClose={handleNotificationClose}
          />
        </Suspense>
      )}

      {/* Click outside to close menus */}
      {(showUserMenu || showMobileMenu) && (
        <div
          className="fixed inset-0 z-dropdown"
          onClick={() => {
            setShowUserMenu(false);
            setShowMobileMenu(false);
          }}
        />
      )}
    </nav>
  );
}

export default Navigation;